import React from 'react';
import { Gift, Calendar, Heart, Star, Sparkles, Crown, Users, Clock } from 'lucide-react';

const OccasionCategoriesPage = ({ setCurrentPage }) => {
  const occasions = [
    {
      id: 'wedding',
      title: 'Bryllupsgaver',
      emoji: '💍',
      color: 'rose',
      description: 'Feir kjærligheten med gaver som varer livet ut',
      season: 'Hele året',
      typical_budget: '500-2000 kr',
      examples: ['Servise og glass', 'Opplevelser for to', 'Personaliserte gavesett']
    },
    {
      id: 'birthday',
      title: 'Bursdagsgaver',
      emoji: '🎂',
      color: 'primary',
      description: 'Gjør bursdager spesielle for alle aldre',
      season: 'Hele året',
      typical_budget: '200-1000 kr',
      examples: ['Personlige gaver', 'Hobby-relatert', 'Opplevelser og minner']
    },
    {
      id: 'christmas',
      title: 'Julegaver',
      emoji: '🎄',
      color: 'success',
      description: 'Spred julens glede og varme',
      season: 'November-Januar',
      typical_budget: '100-800 kr',
      examples: ['Koselige gaver', 'Familie-tradisjon', 'Hjemmelaget kjærlighet']
    },
    {
      id: 'confirmation',
      title: 'Konfirmasjon',
      emoji: '✨',
      color: 'accent',
      description: 'Marker overgangen til voksenlivet',
      season: 'Våren',
      typical_budget: '300-1500 kr',
      examples: ['Smykker og klokker', 'Teknologi', 'Penger og sparing']
    },
    {
      id: 'graduation',
      title: 'Graduering',
      emoji: '🎓',
      color: 'indigo',
      description: 'Feir akademiske prestasjoner og nye begynnelser',
      season: 'Mai-Juni',
      typical_budget: '400-2000 kr',
      examples: ['Profesjonelt utstyr', 'Reise og opplevelser', 'Fremtids-investeringer']
    },
    {
      id: 'retirement',
      title: 'Pensjonering',
      emoji: '🏆',
      color: 'purple',
      description: 'Hedre en lang og meningsfull karriære',
      season: 'Hele året',
      typical_budget: '500-3000 kr',
      examples: ['Minnegaver', 'Hobby og fritid', 'Luksus og komfort']
    },
    {
      id: 'anniversary',
      title: 'Jubileum',
      emoji: '💖',
      color: 'pink',
      description: 'Feir kjærlighet og partnerskap',
      season: 'Hele året',
      typical_budget: '300-2000 kr',
      examples: ['Romantiske opplevelser', 'Smykker', 'Reiser og minner']
    },
    {
      id: 'newborn',
      title: 'Nyfødt baby',
      emoji: '👶',
      color: 'yellow',
      description: 'Ønsk velkommen til verdens nyeste stjerne',
      season: 'Hele året',
      typical_budget: '200-800 kr',
      examples: ['Babyklær og utstyr', 'Keepsakes', 'Praktisk hjelp']
    },
    {
      id: 'housewarming',
      title: 'Innflytting',
      emoji: '🏠',
      color: 'orange',
      description: 'Hjelp dem å gjøre huset til et hjem',
      season: 'Hele året',
      typical_budget: '200-1000 kr',
      examples: ['Interiør og dekor', 'Kjøkkenutstyr', 'Planter og blomster']
    }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-accent-50 to-purple-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">🎉</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Gaver til spesielle anledninger</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              Hver anledning fortjener en spesiell gave. Finn den perfekte gaven som matcher 
              øyeblikkets betydning og skaper minner som varer.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-primary-500 to-accent-600 text-white rounded-lg hover:from-primary-600 hover:to-accent-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Finn anledningsgaver
            </button>
          </div>
        </div>
      </section>

      {/* Occasions Philosophy */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Hvorfor anledninger krever spesielle gaver
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-lg p-6 border border-primary-100 text-center">
                <Calendar className="w-12 h-12 text-primary-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Markerer milepæler</h3>
                <p className="text-secondary-600">
                  Spesielle anledninger representerer viktige øyeblikk i livet. Gaven din bidrar 
                  til å gjøre disse øyeblikkene enda mer minnerike.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-purple-50 rounded-lg p-6 border border-accent-100 text-center">
                <Heart className="w-12 h-12 text-accent-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Uttrykker følelser</h3>
                <p className="text-secondary-600">
                  En gjennomtenkt anledningsgave kommuniserer dine følelser og viser hvor mye 
                  personen og øyeblikket betyr for deg.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100 text-center">
                <Star className="w-12 h-12 text-purple-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Skaper tradisjoner</h3>
                <p className="text-secondary-600">
                  Gode anledningsgaver kan bli en del av familietradisjoner og minner som 
                  deles gjennom generasjoner.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Occasions Grid */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Utforsk alle anledninger
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {occasions.map((occasion) => (
                <div key={occasion.id} className="bg-white rounded-lg shadow-sm border border-secondary-100 overflow-hidden hover:shadow-md transition-shadow">
                  <div className={`bg-gradient-to-r from-${occasion.color}-500 to-${occasion.color}-600 p-6 text-center`}>
                    <div className="text-4xl mb-2">{occasion.emoji}</div>
                    <h3 className="text-xl font-bold text-white mb-2">{occasion.title}</h3>
                    <p className="text-white opacity-90 text-sm">{occasion.description}</p>
                  </div>
                  
                  <div className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-secondary-500">Sesong:</span>
                        <span className="text-secondary-700 font-medium">{occasion.season}</span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-secondary-500">Typisk budsjett:</span>
                        <span className="text-secondary-700 font-medium">{occasion.typical_budget}</span>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-semibold text-secondary-700 mb-2">Populære gavetyper:</h4>
                        <ul className="text-xs text-secondary-600 space-y-1">
                          {occasion.examples.map((example, index) => (
                            <li key={index} className="flex items-center">
                              <span className="w-1.5 h-1.5 bg-secondary-400 rounded-full mr-2"></span>
                              {example}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(occasion.id)}
                        className={`w-full mt-4 px-4 py-2 bg-${occasion.color}-500 hover:bg-${occasion.color}-600 text-white rounded-lg transition-colors font-medium text-sm`}
                      >
                        Utforsk {occasion.title.toLowerCase()}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Seasonal Calendar */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Anledningskalender gjennom året
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 border border-green-100">
                <h3 className="text-lg font-semibold text-secondary-800 mb-4 text-center">🌸 Vår</h3>
                <ul className="space-y-2 text-sm text-secondary-600">
                  <li>• Konfirmasjon (april-juni)</li>
                  <li>• Graduering (mai-juni)</li>
                  <li>• Mors dag (2. søndag i mai)</li>
                  <li>• Nasjonaldag (17. mai)</li>
                  <li>• Valborg (30. april)</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-6 border border-yellow-100">
                <h3 className="text-lg font-semibold text-secondary-800 mb-4 text-center">☀️ Sommer</h3>
                <ul className="space-y-2 text-sm text-secondary-600">
                  <li>• Bryllup-sesong</li>
                  <li>• Student-eksamen</li>
                  <li>• Fars dag (2. søndag i november)</li>
                  <li>• Sommerferie-reiser</li>
                  <li>• Utendørs-feiring</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg p-6 border border-orange-100">
                <h3 className="text-lg font-semibold text-secondary-800 mb-4 text-center">🍂 Høst</h3>
                <ul className="space-y-2 text-sm text-secondary-600">
                  <li>• Tilbake til skole</li>
                  <li>• Halloween (31. oktober)</li>
                  <li>• Fars dag (november)</li>
                  <li>• Pensjonering</li>
                  <li>• Innflytting</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
                <h3 className="text-lg font-semibold text-secondary-800 mb-4 text-center">❄️ Vinter</h3>
                <ul className="space-y-2 text-sm text-secondary-600">
                  <li>• Jul (desember)</li>
                  <li>• Nyttår (1. januar)</li>
                  <li>• Valentines dag (14. februar)</li>
                  <li>• Vintersport-sesong</li>
                  <li>• Julemarked-shopping</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Occasion Planning Tips */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Tips for anledningsgaver
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100">
                  <Clock className="w-8 h-8 text-success-500 mb-4" />
                  <h3 className="text-lg font-semibold text-secondary-800 mb-3">Planlegg i god tid</h3>
                  <ul className="text-sm text-secondary-600 space-y-2">
                    <li>• Start planlegging 2-4 uker før anledningen</li>
                    <li>• For store anledninger (bryllup, jubileum): 2-3 måneder</li>
                    <li>• Sjekk leveringstider, spesielt for personaliserte gaver</li>
                    <li>• Ha en backup-plan hvis noe går galt</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100">
                  <Sparkles className="w-8 h-8 text-accent-500 mb-4" />
                  <h3 className="text-lg font-semibold text-secondary-800 mb-3">Tilpass til anledningen</h3>
                  <ul className="text-sm text-secondary-600 space-y-2">
                    <li>• Formelle anledninger krever mer tradisjonelle gaver</li>
                    <li>• Intime feiring tillater mer personlige og kreative valg</li>
                    <li>• Vurder kulturelle og religiøse tradisjoner</li>
                    <li>• Tenk på om gaven skal åpnes offentlig eller privat</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100">
                  <Crown className="w-8 h-8 text-purple-500 mb-4" />
                  <h3 className="text-lg font-semibold text-secondary-800 mb-3">Gjør det minneverdig</h3>
                  <ul className="text-sm text-secondary-600 space-y-2">
                    <li>• Inkluder en hjertelig og personlig hilsen</li>
                    <li>• Fortell historien bak gavevalgetet ditt</li>
                    <li>• Vurder å ta bilder av overrekkelsen</li>
                    <li>• Velg gaver som kan bli familiearvestykker</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-warning-50 to-orange-50 rounded-lg p-6 border border-warning-100">
                  <Users className="w-8 h-8 text-warning-500 mb-4" />
                  <h3 className="text-lg font-semibold text-secondary-800 mb-3">Koordiner med andre</h3>
                  <ul className="text-sm text-secondary-600 space-y-2">
                    <li>• Sjekk med andre gjester om fellesgaver</li>
                    <li>• Unngå dobbeltgaver ved å kommunisere</li>
                    <li>• Vurder ønskelister og registre</li>
                    <li>• Respekter budsjett-rammer fra arrangørene</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-500 to-purple-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å finne den perfekte anledningsgaven?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              La oss hjelpe deg å velge en gave som gjør anledningen ekstra spesiell
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-primary-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('categories')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-primary-600 transition-colors font-semibold"
              >
                Se alle kategorier
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OccasionCategoriesPage;